import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Stack,
  Divider,
  Tooltip,
  alpha,
  useTheme,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import UpdateIcon from '@mui/icons-material/Update';
import ArticleIcon from '@mui/icons-material/Article';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import GroupIcon from '@mui/icons-material/Group';
import { useTranslation } from 'react-i18next';
import { stringToColor, getInitials, formatDate, getStatusColor } from '../helpers';

// ---------------------------------------------------------------------------
// Sub-sections
// ---------------------------------------------------------------------------
const InfoRow = ({ icon, label, value }) => (
  <Stack direction="row" gap={1.5} alignItems="flex-start">
    <Box sx={{ color: 'text.disabled', flexShrink: 0, mt: 0.1 }}>{icon}</Box>
    <Box>
      <Typography variant="caption" color="text.disabled" display="block" lineHeight={1.3} mb={0.2}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600}>{value}</Typography>
    </Box>
  </Stack>
);

const SectionTitle = ({ children }) => (
  <Typography
    variant="caption"
    color="text.disabled"
    display="block"
    mb={1}
    fontWeight={700}
    textTransform="uppercase"
    letterSpacing={0.8}
    sx={{ fontSize: 10 }}
  >
    {children}
  </Typography>
);

const MetricBox = ({ icon, label, value, color }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        flex: 1,
        p: 1.25,
        borderRadius: 2,
        bgcolor: alpha(color, 0.07),
        border: `1px solid ${alpha(color, 0.15)}`,
        textAlign: 'center',
      }}
    >
      <Box sx={{ color, mb: 0.5, display: 'flex', justifyContent: 'center' }}>{icon}</Box>
      <Typography variant="h6" fontWeight={800} color={color} lineHeight={1}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>
        {label}
      </Typography>
    </Box>
  );
};

const TeamMemberAvatar = ({ user, role }) => {
  const name = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  const color = stringToColor(name);
  return (
    <Tooltip title={`${name} (${role})`}>
      <Avatar
        sx={{
          width: 34,
          height: 34,
          fontSize: 11,
          fontWeight: 700,
          bgcolor: color,
          border: `2px solid white`,
          boxShadow: `0 2px 8px ${alpha(color, 0.4)}`,
          cursor: 'default',
          ml: -0.75,
          '&:first-of-type': { ml: 0 },
        }}
      >
        {getInitials(user.first_name, user.last_name)}
      </Avatar>
    </Tooltip>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const FirmInfoCard = ({ firm, contracts, offers }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const infoRows = [
    firm.luca_code && { label: t('firmDetail.lucaCode'), value: firm.luca_code, icon: <InfoOutlinedIcon sx={{ fontSize: 15 }} /> },
    firm.created_at && { label: t('firmDetail.createdAt'), value: formatDate(firm.created_at), icon: <CalendarTodayIcon sx={{ fontSize: 15 }} /> },
    firm.updated_at && { label: t('firmDetail.updatedAt', 'Son Güncelleme'), value: formatDate(firm.updated_at), icon: <UpdateIcon sx={{ fontSize: 15 }} /> },
    firm.company?.name && { label: t('firmDetail.company'), value: firm.company.name, icon: <BusinessIcon sx={{ fontSize: 15 }} /> },
  ].filter(Boolean);

  const referenceValue = useMemo(() => {
    if (!firm.reference_type || firm.reference_type === 'none') return null;
    if (firm.reference_type === 'existing') {
      return firm.reference_firm
        ? `${firm.reference_firm.firm_name} (${firm.reference_firm.firm_short_name})`
        : '—';
    }
    return firm.reference_text || '—';
  }, [firm]);

  const activeContracts = useMemo(
    () => contracts.filter(c => {
      const name = c.work_status?.name?.toLowerCase() || '';
      return name.includes('devam') || name.includes('aktif') || name.includes('imzaland');
    }).length,
    [contracts]
  );

  const autoRenewalCount = contracts.filter(c => c.auto_renewal).length;
  const givenOffers = offers.filter(o => o.proposal_given).length;

  const teamMap = useMemo(() => {
    const map = new Map();
    contracts.forEach(c => {
      // contracts don't have users in current query
    });
    return map;
  }, [contracts]);

  // Unique team members from job assignments - passed via firm.jobs
  const teamMembers = useMemo(() => {
    const map = new Map();
    (firm.jobs || []).forEach(job => {
      const { technical_user, financial_user } = job.job_assignment || {};
      if (technical_user && !map.has(technical_user.id)) {
        map.set(technical_user.id, { user: technical_user, role: t('firmDetail.technicalUser', 'Teknik') });
      }
      if (financial_user && !map.has(financial_user.id)) {
        map.set(financial_user.id, { user: financial_user, role: t('firmDetail.financialUser', 'Mali') });
      }
    });
    return Array.from(map.values());
  }, [firm.jobs, t]);

  return (
    <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{ px: 2.5, py: 1.75, borderBottom: `1px solid ${theme.palette.divider}`, bgcolor: alpha(theme.palette.text.primary, 0.02) }}>
        <Typography variant="subtitle2" fontWeight={700}>{t('firmDetail.firmInfo')}</Typography>
      </Box>

      <Box sx={{ p: 2.5 }}>
        {/* Info rows */}
        <Stack gap={2}>
          {infoRows.map(row => (
            <InfoRow key={row.label} icon={row.icon} label={row.label} value={row.value} />
          ))}
        </Stack>

        {/* Description */}
        {firm.description && (
          <>
            <Divider sx={{ my: 2 }} />
            <SectionTitle>{t('firmDetail.description')}</SectionTitle>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
              {firm.description}
            </Typography>
          </>
        )}

        {/* Reference */}
        {referenceValue && (
          <>
            <Divider sx={{ my: 2 }} />
            <SectionTitle>{t('firmDetail.referenceType')}</SectionTitle>
            <Typography variant="body2" fontWeight={600}>{referenceValue}</Typography>
          </>
        )}

        {/* Contract Insights */}
        {contracts.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <SectionTitle>{t('firmDetail.contractInsights', 'Sözleşme Özeti')}</SectionTitle>
            <Stack direction="row" gap={1}>
              <MetricBox
                icon={<ArticleIcon sx={{ fontSize: 16 }} />}
                label={t('firmDetail.activeContracts', 'Aktif')}
                value={activeContracts}
                color={theme.palette.primary.main}
              />
              <MetricBox
                icon={<AutorenewIcon sx={{ fontSize: 16 }} />}
                label={t('firmDetail.autoRenewal', 'Oto. Yenileme')}
                value={autoRenewalCount}
                color={theme.palette.success.main}
              />
            </Stack>
          </>
        )}

        {/* Offer Pipeline */}
        {offers.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <SectionTitle>{t('firmDetail.offerPipeline', 'Teklif Durumu')}</SectionTitle>
            <Stack direction="row" gap={1}>
              <MetricBox
                icon={<LocalOfferIcon sx={{ fontSize: 16 }} />}
                label={t('firmDetail.offersGiven', 'Verilen')}
                value={givenOffers}
                color={'#f59e0b'}
              />
              <MetricBox
                icon={<LocalOfferIcon sx={{ fontSize: 16 }} />}
                label={t('firmDetail.offersTotal', 'Toplam')}
                value={offers.length}
                color={theme.palette.text.secondary}
              />
            </Stack>
          </>
        )}

        {/* Team */}
        {teamMembers.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <SectionTitle>
              <Stack direction="row" alignItems="center" gap={0.5} component="span">
                <GroupIcon sx={{ fontSize: 11 }} />
                {t('firmDetail.team', 'Sorumlular')}
              </Stack>
            </SectionTitle>
            <Stack direction="row" alignItems="center" gap={0}>
              {teamMembers.map(({ user, role }) => (
                <TeamMemberAvatar key={user.id} user={user} role={role} />
              ))}
              <Typography variant="caption" color="text.disabled" sx={{ ml: 1.25, fontSize: 11 }}>
                {teamMembers.length} {t('firmDetail.teamMembers', 'kişi')}
              </Typography>
            </Stack>
          </>
        )}
      </Box>
    </Paper>
  );
};

export default FirmInfoCard;
